import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/store/AuthContext";
import { currencySymbol, formatPrice } from "@/pages/home/components/ProductCard";

interface OrderHeader {
  id: number;
  status: string | null;
  currency: string;
  subtotal_items: number | string | null;
  shipping_total: number | string | null;
  tax_total: number | string | null;
  discount_price: number | string | null;
  recipient: Record<string, string> | null;
  created_at: string;
}

interface OrderItem {
  id: number;
  product_id: string;
  product_name: string;
  sku_label: string | null;
  quantity: number;
  unit_price: number | string;
  final_price: number | string | null;
  subtotal: number | string | null;
}

export default function OrderConfirmationPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [order, setOrder] = useState<OrderHeader | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from("order_headers")
          .select("*, order_items(*)")
          .eq("id", id)
          .maybeSingle();
        if (err) throw err;
        if (!data) {
          setError("Pedido não encontrado.");
          return;
        }
        if (alive) {
          const d = data as Record<string, unknown>;
          setOrder({
            id: d.id as number,
            status: d.status as string | null,
            currency: d.currency as string,
            subtotal_items: d.subtotal_items as number | string | null,
            shipping_total: d.shipping_total as number | string | null,
            tax_total: d.tax_total as number | string | null,
            discount_price: d.discount_price as number | string | null,
            recipient: d.recipient as Record<string, string> | null,
            created_at: d.created_at as string,
          });
          setItems((d.order_items ?? []) as OrderItem[]);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Não foi possível carregar o pedido";
        if (alive) setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-[760px] mx-auto w-full px-4 md:px-8 py-12 animate-pulse">
          <div className="h-8 w-2/3 bg-background-200/60 rounded mb-3"></div>
          <div className="h-4 w-1/2 bg-background-200/60 rounded mb-8"></div>
          <div className="h-32 w-full bg-background-200/60 rounded"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <p className="font-heading text-foreground-950 text-2xl mb-4">
              {error ?? "Pedido não encontrado."}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer"
            >
              Voltar à loja
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const symbol = currencySymbol(order.currency);
  const subtotal = Number(order.subtotal_items ?? 0);
  const shipping = Number(order.shipping_total ?? 0);
  const tax = Number(order.tax_total ?? 0);
  const total = subtotal + shipping + tax - Number(order.discount_price ?? 0);
  const recipient = order.recipient ?? {};
  const placedDate = order.created_at
    ? new Date(order.created_at).toLocaleDateString("pt-BR", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="max-w-[760px] mx-auto w-full px-4 md:px-8 pt-12 md:pt-16 pb-16">
          <div className="mb-10">
            <p className="text-[12px] uppercase tracking-[0.18em] text-foreground-600 mb-2">
              Pedido #{order.id} · {placedDate}
            </p>
            <h1 className="font-heading text-foreground-950 text-3xl md:text-4xl mb-3">
              Obrigado{recipient.name ? `, ${recipient.name.split(" ")[0]}` : ""}.
            </h1>
            <p className="text-[14px] text-foreground-700">
              Confirmação enviada para{" "}
              <span className="text-foreground-950">{recipient.email ?? "seu e-mail"}</span>.
            </p>
          </div>

          <ul className="divide-y divide-background-200/70 border-y border-background-200/70 mb-6">
            {items.map((it) => {
              const final = Number(it.final_price ?? it.unit_price ?? 0);
              const subtotalLine = Number(it.subtotal ?? final * it.quantity);
              return (
                <li key={it.id} className="py-4 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-foreground-950 leading-snug">
                      {it.product_name}
                    </p>
                    {it.sku_label && (
                      <p className="text-[12.5px] text-foreground-600 mt-0.5">
                        {it.sku_label}
                      </p>
                    )}
                    <p className="text-[12.5px] text-foreground-600 mt-0.5">
                      Qtd {it.quantity}
                    </p>
                  </div>
                  <p className="text-[14px] text-foreground-950 whitespace-nowrap">
                    {symbol}{formatPrice(subtotalLine)}
                  </p>
                </li>
              );
            })}
          </ul>

          <dl className="space-y-2 text-[13.5px] mb-10">
            <div className="flex justify-between text-foreground-700">
              <dt>Subtotal</dt>
              <dd className="text-foreground-950">
                {symbol}{formatPrice(subtotal)}
              </dd>
            </div>
            <div className="flex justify-between text-foreground-700">
              <dt>Frete</dt>
              <dd className="text-foreground-950">
                {shipping === 0 ? "Grátis" : `${symbol}${formatPrice(shipping)}`}
              </dd>
            </div>
            {tax > 0 && (
              <div className="flex justify-between text-foreground-700">
                <dt>Impostos</dt>
                <dd className="text-foreground-950">
                  {symbol}{formatPrice(tax)}
                </dd>
              </div>
            )}
            <div className="flex justify-between pt-3 border-t border-background-200/70">
              <dt className="text-[14px] text-foreground-950 font-medium">Total</dt>
              <dd className="font-heading text-foreground-950 text-xl">
                {symbol}{formatPrice(total)}
              </dd>
            </div>
          </dl>

          <div className="mb-10">
            <p className="text-[12px] uppercase tracking-[0.16em] text-foreground-950 font-medium mb-2">
              Enviar para
            </p>
            <p className="text-[14px] text-foreground-900 leading-6">
              {recipient.name ?? "—"}<br />
              {recipient.address ?? ""}<br />
              {[recipient.city, recipient.state, recipient.zip].filter(Boolean).join(", ")}
              {recipient.country && <><br />{recipient.country}</>}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className="px-5 h-11 inline-flex items-center gap-2 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Continuar comprando
            </Link>
            {user && (
              <Link
                to="/account/orders"
                className="px-5 h-11 inline-flex items-center gap-2 rounded-full border border-background-300 hover:border-foreground-900 text-foreground-900 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Meus pedidos
              </Link>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}